<template>
  <article
    :aria-labelledby="item.name && item.show_title ? headingId : undefined"
    class="w-full px-0 md:px-8"
  >
    <div class="bg-white border-round flex flex-column gap-3 px-2 md:px-3 py-3 md:py-4">
      <h2
        v-if="item.name && item.show_title"
        :id="headingId"
        :class="[getTextClass(web_content_type), 'sm:text-3xl md:text-4xl my-0']"
      >
        {{ item.name }}
      </h2>

      <section
        v-if="programmeRequirements.length || additionalProgrammeRequirements.length"
        :aria-labelledby="requirementsHeadingId"
        class="px-0 sm:px-4"
      >
        <div class="w-full">
          <h3 :id="requirementsHeadingId" class="text-lg text-900 mt-0 mb-3">
            Programme requirements
            <span
              aria-hidden="true"
              class="block w-2rem border-round mt-2"
              style="height: 0.15rem; background: var(--sats-gold)"
            />
          </h3>

          <div v-if="programmeRequirements.length" class="grid">
            <article
              v-for="requirement in programmeRequirements"
              :key="requirement.key"
              :class="[
                'col-12',
                {
                  'lg:col-6': programmeRequirements.length > 1,
                },
              ]"
            >
              <div class="flex align-items-start gap-3 h-full">
                <Avatar
                  :icon="requirement.icon"
                  shape="circle"
                  class="bg-primary-50 text-primary flex-shrink-0"
                />

                <div class="flex-1 min-w-0">
                  <h4 class="text-base font-semibold text-900 mt-0 mb-2">
                    {{ requirement.name }}
                  </h4>
                  <p v-if="requirement.description" class="text-sm text-600 line-height-3 m-0">
                    {{ requirement.description }}
                  </p>
                </div>
              </div>
            </article>
          </div>

          <div v-if="additionalProgrammeRequirements.length" class="w-full">
            <Divider v-if="programmeRequirements.length" />

            <h4 :id="additionalRequirementsHeadingId" class="text-base text-900 mt-0 mb-2">
              Additional requirements
            </h4>

            <p
              v-if="
                additionalProgrammeRequirements.some((requirement) => requirement.subjects.length)
              "
              class="text-sm text-500 font-italic mt-0 mb-2"
            >
              Select a requirement to filter the courses shown below.
            </p>

            <ol
              class="w-full grid list-none p-0 m-0"
              :aria-labelledby="additionalRequirementsHeadingId"
            >
              <li
                v-for="requirement in additionalProgrammeRequirements"
                :key="requirement.key"
                :class="[
                  'col-12 px-0 py-2',
                  {
                    'lg:col-6 lg:p-2': additionalProgrammeRequirements.length > 1,
                  },
                ]"
              >
                <component
                  :is="requirement.subjects.length ? 'Button' : 'article'"
                  :class="[
                    'surface-50 border-3 border-round-lg p-2 md:p-3 w-full h-full focus:shadow-none',
                    {
                      'border-primary-400': isRequirementSelected(requirement),
                    },
                  ]"
                  style="border-color: var(--sats-border)"
                  @click="subjectIDs = requirement.subjects"
                >
                  <div class="w-full flex flex-column align-items-start gap-2">
                    <div class="w-full flex justify-content-between align-items-center gap-2">
                      <h5 class="text-base font-semibold text-900 m-0">
                        {{ requirement.name }}
                      </h5>
                      <Tag
                        v-if="requirement.valueLabel"
                        rounded
                        :icon="requirement.icon"
                        :value="requirement.valueLabel"
                        class="align-self-start sm:align-self-center flex-shrink-0"
                        :pt="requirementTagPassThrough"
                      />
                    </div>
                    <p v-if="requirement.description" class="text-sm text-600 line-height-3 m-0">
                      {{ requirement.description }}
                    </p>
                  </div>
                </component>
              </li>
            </ol>
          </div>
        </div>
      </section>

      <div
        v-if="subjectIDs.length"
        class="flex align-items-center justify-content-between gap-2 px-0 sm:px-4"
      >
        <div class="text-sm text-500 font-italic m-0">
          {{ selectedRequirement.name }} ({{ subjectIDs.length }} course{{
            subjectIDs.length === 1 ? '' : 's'
          }})
        </div>
        <Button
          size="small"
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          severity="secondary"
          @click="subjectIDs = []"
        />
      </div>

      <p v-if="showYearTimeline" class="text-sm text-500 font-italic px-0 sm:px-4 m-0">
        Select a year below to view its courses.
      </p>

      <Stepper
        :key="isSubjectFilterActive ? 'filtered-subjects' : 'all-subjects'"
        orientation="horizontal"
        :aria-label="showYearTimeline ? 'Programme years' : 'Programme courses'"
        aria-orientation="horizontal"
        class="w-full mx-auto px-0 sm:px-4"
        :pt="stepperPassThrough"
      >
        <StepperPanel
          v-for="yearGroup in subjectsByYear"
          :key="yearGroup.year"
          :header="`Year ${yearGroup.year}`"
        >
          <template #content>
            <DataTable
              v-model:expandedRowGroups="expandedSubjectGroups[yearGroup.year]"
              :value="yearGroup.subjects"
              dataKey="subjectKey"
              expandableRowGroups
              rowGroupMode="subheader"
              groupRowsBy="subjectGroupKey"
              sortMode="single"
              sortField="subjectGroupKey"
              :sortOrder="1"
              size="small"
              scrollable
              scrollHeight="min(36rem, 65vh)"
              responsiveLayout="scroll"
              tableStyle="min-width: min(46rem, 100%)"
              class="w-full surface-50"
              :pt="subjectTablePassThrough"
              :tableProps="{
                'aria-label': getSubjectTableAriaLabel(yearGroup.year),
              }"
              @row-click="openSubject($event.data)"
            >
              <template #groupheader="{ data }">
                <span class="inline-flex align-items-center gap-2 ml-2 vertical-align-middle">
                  <i :class="[data.subjectGroupIcon, 'text-primary']" aria-hidden="true" />
                  <span class="font-semibold text-base md:text-lg">
                    {{ data.subjectGroupName }}
                  </span>
                  <Tag
                    rounded
                    :value="data.subjectGroupCount"
                    :aria-label="`${data.subjectGroupCount} courses`"
                    class="flex-shrink-0"
                    :pt="tagPassThrough"
                  />
                </span>
              </template>

              <Column field="subjectGroupKey" />

              <Column
                field="code"
                header="Code"
                header-class="pl-3 white-space-nowrap"
                body-class="pl-3 white-space-nowrap"
                style="width: 1%"
              >
                <template #body="{ data: subject }">
                  <Tag rounded :value="subject.code" class="flex-shrink-0" :pt="tagPassThrough" />
                </template>
              </Column>

              <Column field="name" header="Course" style="width: 100%">
                <template #body="{ data: subject }">
                  <span class="text-color line-height-3">
                    {{ subject.name }}
                  </span>
                </template>
              </Column>

              <Column
                field="credits"
                header="Credits"
                header-class="text-right white-space-nowrap pr-3"
                body-class="text-right white-space-nowrap text-sm text-600 pr-3"
                style="width: 1%"
              >
                <template #body="{ data: subject }">
                  {{ getCreditLabel(subject.credits) }}
                </template>
              </Column>
            </DataTable>
          </template>
        </StepperPanel>
      </Stepper>
    </div>
  </article>
</template>

<script setup>
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  web_content_type: {
    type: Object,
    required: true,
  },
  programme: {
    type: Object,
    default: () => ({}),
  },
})

const subjectIDs = ref([])
const isSubjectFilterActive = computed(() => subjectIDs.value.length > 0)

const isRequirementSelected = (requirement) =>
  subjectIDs.value.length > 0 &&
  subjectIDs.value.length === requirement.subjects.length &&
  requirement.subjects.every((subjectId, index) => subjectId === subjectIDs.value[index])

const filterSubjects = (subjects) => {
  const subjectIds = subjectIDs.value
  if (!Array.isArray(subjectIds) || !subjectIds.length) return subjects

  return subjects.filter((subject) => subjectIds.includes(subject.subject_id))
}

const headingId = `subjects-${props.item.id ?? props.item.web_section_id}-heading`
const cleanText = (value) => (typeof value === 'string' ? value.trim() : '')

const numberFormat = new Intl.NumberFormat('en-ZA', {
  maximumFractionDigits: 2,
})

const createRequirement = (key, name, description, fallbackName, icon) => {
  const cleanName = cleanText(name)
  const cleanDescription = cleanText(description)

  if (!cleanName && !cleanDescription) return null

  return {
    key,
    name: cleanName || fallbackName,
    description: cleanDescription,
    icon,
  }
}

const programmeRequirements = computed(() => {
  const totalCreditsFallback =
    props.programme.credits === null || props.programme.credits === undefined
      ? 'Total Credits'
      : `Total Credits (${props.programme.credits})`

  return [
    createRequirement(
      'required-subjects',
      props.programme.required_subjects_requirement_name,
      props.programme.required_subjects_requirement_description,
      'Compulsory Courses',
      'pi pi-check-circle'
    ),
    createRequirement(
      'total-credits',
      props.programme.total_credits_requirement_name,
      props.programme.total_credits_requirement_description,
      totalCreditsFallback,
      'pi pi-book'
    ),
  ].filter(Boolean)
})

const getRequirementValueLabel = (value, byCredits) => {
  if (value === null || value === undefined || value === '') return ''

  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return ''

  const singularUnit = byCredits ? 'credit' : 'subject'
  const unit = numericValue === 1 ? singularUnit : `${singularUnit}s`

  return `${numberFormat.format(numericValue)} ${unit} required`
}

const getRequirementRowOrder = (value) => {
  if (value === null || value === undefined || value === '') return Number.MAX_SAFE_INTEGER

  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : Number.MAX_SAFE_INTEGER
}

const additionalProgrammeRequirements = computed(() => {
  const requirements = Array.isArray(props.programme.programme_requirements)
    ? props.programme.programme_requirements
    : []

  return requirements
    .map((requirement, index) => {
      const name = cleanText(requirement?.name)
      const description = cleanText(requirement?.description)
      const byCredits = requirement?.by_credits === true
      const rowOrder = getRequirementRowOrder(requirement?.row_order)
      const valueLabel = getRequirementValueLabel(requirement?.value, byCredits)

      if (!name && !description && !valueLabel) return null

      return {
        key: `additional-requirement-${rowOrder}-${index}`,
        name: name || 'Additional requirement',
        description,
        subjects: requirement?.subject_ids ?? [],
        icon: byCredits ? 'pi pi-book' : 'pi pi-list',
        rowOrder,
        sourceIndex: index,
        valueLabel,
      }
    })
    .filter(Boolean)
    .sort(
      (firstRequirement, secondRequirement) =>
        firstRequirement.rowOrder - secondRequirement.rowOrder ||
        firstRequirement.sourceIndex - secondRequirement.sourceIndex
    )
})

const selectedRequirement = computed(
  () =>
    additionalProgrammeRequirements.value.find((requirement) =>
      isRequirementSelected(requirement)
    ) ?? null
)

const requirementsHeadingId = `programme-${
  props.programme.id ?? props.item.object_id
}-subjects-${props.item.id ?? props.item.web_section_id}-requirements`
const additionalRequirementsHeadingId = `${requirementsHeadingId}-additional`

const tagPassThrough = {
  root: {
    class: 'bg-primary-50 text-primary',
  },
}

const requirementTagPassThrough = {
  root: {
    class: 'bg-primary-50 text-primary border-none px-3 py-2',
  },
  icon: {
    'aria-hidden': 'true',
  },
}

const openSubject = (subject) => {
  if (!subject?.link) return

  return navigateTo(subject.link)
}

const onSubjectRowKeydown = (event, subject) => {
  if (event.target !== event.currentTarget) return
  if (!['Enter', ' ', 'Spacebar'].includes(event.key)) return

  event.preventDefault()
  openSubject(subject)
}

const subjectTablePassThrough = {
  rowGroupToggler: ({ instance }) => ({
    'aria-expanded': instance.isRowGroupExpanded,
    'aria-label': `${instance.isRowGroupExpanded ? 'Collapse' : 'Expand'} ${
      instance.rowData.subjectGroupName
    }`,
  }),
  bodyRow: ({ instance }) => {
    const subject = instance.rowData
    const isNavigable = Boolean(subject?.link)

    return {
      tabindex: isNavigable ? 0 : undefined,
      class: isNavigable ? 'cursor-pointer transition-colors hover:surface-100' : undefined,
      'aria-label': isNavigable ? `Open subject ${subject.code}: ${subject.name}` : undefined,
      onKeydown: isNavigable ? (event) => onSubjectRowKeydown(event, subject) : undefined,
    }
  },
}

const { data: subjects } = await useAttachmentItems(() => props.item)

const isMasterOfDivinity = computed(
  () => cleanText(props.programme.name).toLowerCase() === 'master of divinity'
)
const shouldGroupSubjectsByYear = computed(
  () => !isMasterOfDivinity.value && !isSubjectFilterActive.value
)

const subjectsByYear = computed(() => {
  const yearGroups = new Map()

  for (const subject of filterSubjects(subjects.value ?? [])) {
    let year = 1

    if (shouldGroupSubjectsByYear.value) {
      if (subject.year === null || subject.year === undefined) continue

      year = Number(subject.year)
      if (!Number.isFinite(year)) continue
    }

    if (!yearGroups.has(year)) {
      yearGroups.set(year, {
        year,
        compulsory: [],
        elective: [],
      })
    }

    const group = yearGroups.get(year)
    if (subject.required) {
      group.compulsory.push(subject)
    } else {
      group.elective.push(subject)
    }
  }

  return [...yearGroups.values()]
    .sort((first, second) => first.year - second.year)
    .map((yearGroup) => {
      const subjectGroups = [
        {
          key: 'compulsory',
          name: 'Compulsory Courses',
          icon: 'pi pi-check-circle',
          subjects: yearGroup.compulsory,
        },
        {
          key: 'elective',
          name: 'Elective Courses',
          icon: 'pi pi-list',
          subjects: yearGroup.elective,
        },
      ].filter((subjectGroup) => subjectGroup.subjects.length)

      return {
        year: yearGroup.year,
        subjects: subjectGroups.flatMap((subjectGroup) =>
          subjectGroup.subjects.map((subject) => ({
            ...subject,
            subjectKey: getSubjectKey(subject),
            subjectGroupKey: subjectGroup.key,
            subjectGroupName: subjectGroup.name,
            subjectGroupIcon: subjectGroup.icon,
            subjectGroupCount: subjectGroup.subjects.length,
          }))
        ),
      }
    })
})

const showYearTimeline = computed(
  () => shouldGroupSubjectsByYear.value && subjectsByYear.value.length > 1
)

const getSubjectTableAriaLabel = (year) => {
  if (isSubjectFilterActive.value) return 'Filtered programme courses grouped by requirement'

  return isMasterOfDivinity.value
    ? 'Master of Divinity courses grouped by requirement'
    : `Year ${year} courses grouped by requirement`
}

const stepperPassThrough = computed(() => ({
  nav: {
    class: showYearTimeline.value ? undefined : 'hidden',
  },
  panelContainer: {
    class: 'min-w-0 p-0 overflow-hidden border-1 border-round-2xl',
    style: {
      color: 'var(--sats-ink)',
      borderColor: 'var(--sats-border)',
      boxShadow: '0 0.4rem 1.4rem rgba(28, 59, 48, 0.045)',
    },
  },
}))

const getSubjectKey = (subject) =>
  `${subject.programme_id}-${subject.subject_id ?? subject.object_id}`

const expandedSubjectGroups = reactive({})

watch(
  () => subjectIDs.value,
  (subjectIds) => {
    if (!Array.isArray(subjectIds) || !subjectIds.length) return

    for (const yearGroup of subjectsByYear.value) {
      expandedSubjectGroups[yearGroup.year] = [
        ...new Set(filterSubjects(yearGroup.subjects).map((subject) => subject.subjectGroupKey)),
      ]
    }
  }
)

watch(
  subjectsByYear,
  (yearGroups) => {
    for (const yearGroup of yearGroups) {
      if (expandedSubjectGroups[yearGroup.year] !== undefined) continue

      expandedSubjectGroups[yearGroup.year] = [
        ...new Set(yearGroup.subjects.map((subject) => subject.subjectGroupKey)),
      ]
    }
  },
  { immediate: true }
)

const getCreditLabel = (credits) => {
  if (credits === null || credits === undefined || credits === '') return ''

  const numericCredits = Number(credits)
  if (!Number.isFinite(numericCredits)) return ''

  const unit = numericCredits === 1 ? 'credit' : 'credits'
  return `${numberFormat.format(numericCredits)} ${unit}`
}
</script>
